import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { ApiError, api, unwrap } from '../api/client.js';
import { Button, Checkbox, LinkButton, Loading, Notice, TextField } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { toLanguage } from '../i18n.js';
import { ME_KEY } from '../session.js';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AuthCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <h1 className="mb-5 text-2xl font-bold">{title}</h1>
      {children}
    </>
  );
}

function useFieldErrors() {
  const [errors, setErrors] = useState<Record<string, string>>({});
  return { errors, setErrors, clear: () => setErrors({}) };
}

export function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const client = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { errors, setErrors } = useFieldErrors();
  const login = useMutation({
    mutationFn: () => unwrap(api.POST('/auth/login', { body: { email, password } })),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ME_KEY });
      const next = params.get('next');
      // Only paths of this app: an open redirect would help phishing.
      void navigate(next?.startsWith('/') && !next.startsWith('//') ? next : '/');
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const found: Record<string, string> = {};
    if (!EMAIL.test(email)) found.email = t('errors.email');
    if (!password) found.password = t('errors.required');
    setErrors(found);
    if (Object.keys(found).length === 0) login.mutate();
  };
  return (
    <AuthCard title={t('auth.login.title')}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {login.isError ? <Notice kind="error">{errorMessage(login.error, t)}</Notice> : null}
        <TextField
          label={t('auth.email')}
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
          required
        />
        <TextField
          label={t('auth.password')}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={errors.password}
          required
        />
        <Button type="submit" busy={login.isPending}>
          {t('auth.login.submit')}
        </Button>
      </form>
      <p className="mt-3">
        <LinkButton to="/forgot-password" variant="soft" size="sm">
          {t('auth.login.forgot')}
        </LinkButton>
      </p>
      <div className="mt-6 flex flex-col gap-2 border-t border-slate-200 pt-5">
        <p className="text-slate-700">{t('auth.login.noAccount')}</p>
        <LinkButton to="/register" variant="secondary" className="w-full">
          {t('auth.login.register')}
        </LinkButton>
      </div>
    </AuthCard>
  );
}

/** Key of the address to prefill in "Website hinzufügen" after registering from a Quick-Check. */
export const PENDING_SITE_KEY = 'wg.pendingSiteUrl';

export function Register() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const quickCheckId = params.get('quickCheck');
  const pendingUrl = params.get('url');
  useEffect(() => {
    if (!pendingUrl) return;
    try {
      localStorage.setItem(PENDING_SITE_KEY, pendingUrl);
    } catch {
      // not remembered: the visitor types the address again
    }
  }, [pendingUrl]);
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [terms, setTerms] = useState(false);
  const { errors, setErrors } = useFieldErrors();
  const register = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/auth/register', {
          body: {
            email,
            password,
            acceptTerms: true,
            locale: toLanguage(i18n.language),
            ...(company.trim() ? { companyName: company.trim() } : {}),
            ...(quickCheckId && /^[0-9a-f-]{36}$/i.test(quickCheckId) ? { quickCheckId } : {}),
          },
        }),
      ),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const found: Record<string, string> = {};
    if (!EMAIL.test(email)) found.email = t('errors.email');
    if (password.length < 12) found.password = t('errors.weakPassword.too_short');
    if (!terms) found.terms = t('errors.terms');
    setErrors(found);
    if (Object.keys(found).length === 0) register.mutate();
  };

  if (register.isSuccess) {
    return (
      <AuthCard title={t('auth.register.sentTitle')}>
        <Notice kind="success">{t('auth.register.sentText', { email })}</Notice>
      </AuthCard>
    );
  }
  const passwordError =
    errors.password ??
    (register.error instanceof ApiError && register.error.code === 'WEAK_PASSWORD'
      ? errorMessage(register.error, t)
      : undefined);
  return (
    <AuthCard title={t('auth.register.title')}>
      <p className="mb-4 text-slate-800">{t('auth.register.intro')}</p>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {register.isError && !passwordError ? (
          <Notice kind="error">{errorMessage(register.error, t)}</Notice>
        ) : null}
        <TextField
          label={t('auth.register.company')}
          optional
          autoComplete="organization"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        />
        <TextField
          label={t('auth.email')}
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={errors.email}
          required
        />
        <TextField
          label={t('auth.password')}
          type="password"
          autoComplete="new-password"
          hint={t('auth.register.passwordHint')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={passwordError}
          required
        />
        <Checkbox
          label={t('auth.register.terms')}
          checked={terms}
          onChange={(e) => setTerms(e.target.checked)}
          error={errors.terms}
        />
        <Button type="submit" busy={register.isPending}>
          {t('auth.register.submit')}
        </Button>
      </form>
      <div className="mt-6 flex flex-col gap-2 border-t border-slate-200 pt-5">
        <p className="text-slate-700">{t('auth.register.haveAccount')}</p>
        <LinkButton to="/login" variant="secondary" className="w-full">
          {t('auth.login.submit')}
        </LinkButton>
      </div>
    </AuthCard>
  );
}

export function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string>();
  const request = useMutation({
    mutationFn: () => unwrap(api.POST('/auth/forgot-password', { body: { email } })),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!EMAIL.test(email)) return setError(t('errors.email'));
    setError(undefined);
    request.mutate();
  };
  return (
    <AuthCard title={t('auth.forgot.title')}>
      {request.isSuccess ? (
        <Notice kind="success">{t('auth.forgot.sent')}</Notice>
      ) : (
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          <p>{t('auth.forgot.intro')}</p>
          {request.isError ? <Notice kind="error">{errorMessage(request.error, t)}</Notice> : null}
          <TextField
            label={t('auth.email')}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={error}
            required
          />
          <Button type="submit" busy={request.isPending}>
            {t('auth.forgot.submit')}
          </Button>
        </form>
      )}
      <p className="mt-4">
        <LinkButton to="/login" variant="soft">
          {t('auth.reset.toLogin')}
        </LinkButton>
      </p>
    </AuthCard>
  );
}

export function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const reset = useMutation({
    mutationFn: () => unwrap(api.POST('/auth/reset-password', { body: { token, password } })),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 12) return setError(t('errors.weakPassword.too_short'));
    setError(undefined);
    reset.mutate();
  };
  if (!token)
    return (
      <AuthCard title={t('auth.reset.title')}>
        <Notice kind="error">{t('auth.verify.missingToken')}</Notice>
      </AuthCard>
    );
  return (
    <AuthCard title={t('auth.reset.title')}>
      {reset.isSuccess ? (
        <>
          <Notice kind="success">{t('auth.reset.done')}</Notice>
          <p className="mt-4">
            <LinkButton to="/login" variant="soft">
              {t('auth.reset.toLogin')}
            </LinkButton>
          </p>
        </>
      ) : (
        <form onSubmit={submit} noValidate className="flex flex-col gap-4">
          {reset.isError &&
          !(reset.error instanceof ApiError && reset.error.code === 'WEAK_PASSWORD') ? (
            <Notice kind="error">{errorMessage(reset.error, t)}</Notice>
          ) : null}
          <TextField
            label={t('auth.reset.password')}
            type="password"
            autoComplete="new-password"
            hint={t('auth.register.passwordHint')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={
              error ??
              (reset.error instanceof ApiError && reset.error.code === 'WEAK_PASSWORD'
                ? errorMessage(reset.error, t)
                : undefined)
            }
            required
          />
          <Button type="submit" busy={reset.isPending}>
            {t('auth.reset.submit')}
          </Button>
        </form>
      )}
    </AuthCard>
  );
}

/** Runs a one-time link action once (also under React's StrictMode double effects). */
function useOnce(run: () => void, enabled: boolean) {
  const done = useRef(false);
  useEffect(() => {
    if (!enabled || done.current) return;
    done.current = true;
    run();
  }, [enabled, run]);
}

export function VerifyEmail() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const client = useQueryClient();
  const verify = useMutation({
    mutationFn: () => unwrap(api.POST('/auth/verify-email', { body: { token } })),
    onSuccess: () => client.invalidateQueries({ queryKey: ME_KEY }),
  });
  useOnce(() => verify.mutate(), Boolean(token));
  return (
    <AuthCard title={t('auth.verify.title')}>
      {!token ? <Notice kind="error">{t('auth.verify.missingToken')}</Notice> : null}
      {verify.isPending ? <p role="status">{t('auth.verify.checking')}</p> : null}
      {verify.isError ? <Notice kind="error">{errorMessage(verify.error, t)}</Notice> : null}
      {verify.isSuccess ? (
        <>
          <Notice kind="success">{t('auth.verify.success')}</Notice>
          <p className="mt-4">
            <LinkButton to="/" variant="soft">
              {t('auth.verify.toOverview')}
            </LinkButton>
          </p>
        </>
      ) : null}
    </AuthCard>
  );
}

export function ConfirmRecipient() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const confirm = useMutation({
    mutationFn: () => unwrap(api.POST('/alert-channels/confirm', { body: { token } })),
  });
  useOnce(() => confirm.mutate(), Boolean(token));
  return (
    <AuthCard title={t('confirmRecipient.title')}>
      {!token ? <Notice kind="error">{t('auth.verify.missingToken')}</Notice> : null}
      {confirm.isPending ? <Loading /> : null}
      {confirm.isError ? <Notice kind="error">{errorMessage(confirm.error, t)}</Notice> : null}
      {confirm.isSuccess ? (
        <Notice kind="success">
          {t('confirmRecipient.success', {
            target: confirm.data.target,
            account: confirm.data.accountName,
          })}
        </Notice>
      ) : null}
    </AuthCard>
  );
}
